import { useInfiniteQuery } from "@tanstack/react-query";
import { api } from "@/lib/backend";
import { useMe } from "@/lib/hooks/use-me";

export function useMyApplications(projectId?: string) {
  const { me } = useMe();
  const ITEMS_PER_PAGE = 10;

  const { data, fetchNextPage, hasNextPage, refetch } = useInfiniteQuery({
    queryKey: ["applications", me?.employee?.id, ITEMS_PER_PAGE, projectId],
    queryFn: async (args) => {
      const res = await api.application.getAll({
        pagination: {
          take: ITEMS_PER_PAGE,
          page: args.pageParam + 1,
        },
        filter: {
          ...(projectId && {
            "availabilitySlot.project.id": {
              value: projectId,
              operator: "equals",
            },
          }),
          "employee.id": {
            value: me?.employee?.id,
            operator: "equals",
          },
          "availabilitySlot.startAt": {
            value: new Date().toISOString(),
            operator: "gte",
          },
        },
        include: {
          availabilitySlot: {
            select: true,
            project: {
              select: true,
            },
            userFunction: {
              select: true,
            },
          },
        },
        sort: {
          field: "availabilitySlot.startAt",
          order: "asc",
        },
      });
      return res.data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const totalItems = lastPage.totalAmount;
      const currentItems = (lastPageParam + 1) * ITEMS_PER_PAGE;

      if (currentItems >= totalItems) {
        return undefined;
      }

      return lastPageParam + 1;
    },
    getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
      return firstPageParam - 1;
    },
    enabled: !!me?.employee?.id,
  });

  const loadMore = async (ev: CustomEvent) => {
    await fetchNextPage();
    if (ev.target) {
      (ev.target as any).complete();
    }
  };

  const applications =
    data?.pages.flatMap((page) => page.items).filter((application) => application !== undefined) ??
    [];

  return {
    applications,
    loadMore,
    hasNextPage,
    refetch,
  };
}
